import { useEffect, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Copy01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { toast } from 'sonner';

// useIsDark detecta si el tema activo de la app es oscuro. No existe un
// ThemeContext/useTheme compartido: ThemeToggle aplica la clase (dark/light/
// purple) sobre document.documentElement y la persiste en localStorage. Aquí se
// lee la clase 'dark' del <html> y se re-evalúa ante cambios con un
// MutationObserver sobre el atributo class. El tema 'purple' NO lleva la clase
// 'dark', así que se trata como claro (sus fondos son claros) — correcto para
// el resaltado (oneLight sobre superficie clara).
function useIsDark() {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false,
  );

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

// SqlBlock encapsula el bloque "SQL ejecutado (solo lectura)" con resaltado de
// sintaxis legible en claro y oscuro. Mantiene el toggle ver/ocultar y la
// legibilidad del label (text-body) ya corregida; la mejora es el highlighting.
export default function SqlBlock({ sql }) {
  const [verSQL, setVerSQL] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const isDark = useIsDark();

  if (!sql) return null;

  const copiarSQL = async () => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopiado(true);
      toast.success('SQL copiado');
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      toast.error('No se pudo copiar el SQL');
    }
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button
          type="button"
          onClick={() => setVerSQL((v) => !v)}
          aria-expanded={verSQL}
          className="rounded font-medium text-secondary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1">
          {verSQL ? 'ocultar SQL' : 'ver detalle / SQL'}
        </button>
      </div>
      {verSQL && (
        <div className="mt-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            {/* Label visible en claro y oscuro (antes text-muted, ilegible). */}
            <p className="text-xs font-medium text-body">SQL ejecutado (solo lectura)</p>
            <button
              type="button"
              onClick={copiarSQL}
              aria-label={copiado ? 'SQL copiado' : 'Copiar SQL'}
              title={copiado ? 'SQL copiado' : 'Copiar SQL'}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-body transition hover:bg-muted hover:text-text focus:outline-none focus-visible:border-primary">
              <HugeiconsIcon icon={copiado ? Tick02Icon : Copy01Icon} size={14} strokeWidth={1.5} aria-hidden={true} />
            </button>
          </div>
          <SyntaxHighlighter
            language="sql"
            style={isDark ? oneDark : oneLight}
            wrapLongLines
            customStyle={{
              margin: 0,
              borderRadius: '0.75rem',
              fontSize: '12px',
              border: '1px solid hsl(var(--border))',
            }}>
            {sql}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
}
